import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Plus } from "lucide-react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

/**
 * V3.1 replacement for the wide "Why Riversol Is Different" comparison
 * table.
 *
 * Desktop: 3-column always-open editorial table (feature | Riversol ✓ |
 * Others ✗). Description is inline under each feature title — no toggle,
 * no accordion, so shoppers see the full differentiator story without a
 * tap.
 *
 * Mobile: descriptions collapse behind a `+` toggle at the LEFT of each
 * row to keep the list scannable and thumb-friendly. Multiple rows can
 * stay open simultaneously (a row only closes when tapped again) —
 * matches the "keep prior tabs open" behaviour rolled out across the
 * rest of the PDP accordions.
 */
export const WhyRiversolV31 = ({ titleOverride }) => {
  const { logo, why } = useProduct();
  // Mobile-only expand tracking (Set so multiple rows can stay open).
  const [mobileOpen, setMobileOpen] = useState(() => new Set());
  const toggleMobile = (i) => setMobileOpen((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  if (!why?.comparison) return null;

  return (
    <section
      className="py-20 lg:py-32"
      data-testid="benefits-why-blue"
      data-inverted-bg="blue"
    >
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT — sticky title */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.05] tracking-tight font-semibold text-[#0F2E2E]">
                {(titleOverride || why.title || "Why Riversol Is Different")
                  .split(" ")
                  .reduce((acc, w, i, arr) => {
                    const mid = Math.ceil(arr.length / 2);
                    if (i < mid) acc[0] = (acc[0] ? acc[0] + " " : "") + w;
                    else acc[1] = (acc[1] ? acc[1] + " " : "") + w;
                    return acc;
                  }, ["", ""])
                  .filter(Boolean)
                  .map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
              </h2>
              <p className="text-[15px] leading-relaxed text-[#0F2E2E]/70 mt-5 max-w-sm">
                Every row is a real difference — measured, formulated,
                dermatologist-signed.
              </p>
            </div>
          </div>

          {/* RIGHT — comparison list */}
          <div className="lg:col-span-7">
            <Reveal>
              {/* Desktop-only column headers */}
              <div
                className="hidden lg:grid items-end gap-x-6 border-b border-[#0F2E2E]/15 pb-4 mb-2"
                style={{ gridTemplateColumns: "1fr 88px 88px" }}
              >
                <div />
                <div className="flex justify-center">
                  <img
                    src={logo}
                    alt="Riversol"
                    className="h-[18px] w-auto max-w-full opacity-90"
                    data-testid="why-riversol-logo"
                  />
                </div>
                <div className="text-center font-mono text-[10px] tracking-[0.22em] uppercase text-[#0F2E2E]/55">
                  Others
                </div>
              </div>

              <ul className="divide-y divide-[#0F2E2E]/12">
                {why.comparison.map((row, i) => {
                  const isMobileOpen = mobileOpen.has(i);
                  return (
                    <li key={i} data-testid={`why-row-${i}`}>
                      <div
                        className="py-6 md:py-7 lg:grid lg:items-center lg:gap-x-6"
                        style={{ gridTemplateColumns: "1fr 88px 88px" }}
                      >
                        {/* Feature block */}
                        <div className="min-w-0">
                          {/* Row header — mobile lays out as [01 | title ....... +],
                              desktop hides the toggle and shows [01 | title]. */}
                          <div className="flex items-start gap-3">
                            <span className="font-mono text-[11px] text-[#0F2E2E]/55 tabular-nums shrink-0 mt-[3px]">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="flex-1 text-[16px] md:text-[17px] font-medium text-[#0F2E2E] leading-snug">
                              {row.feature}
                            </span>
                            {/* Mobile-only + toggle at the right */}
                            <button
                              type="button"
                              onClick={() => toggleMobile(i)}
                              aria-expanded={isMobileOpen}
                              aria-controls={`why-desc-${i}`}
                              data-testid={`why-toggle-mobile-${i}`}
                              className="lg:hidden shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded-full border border-[#0F2E2E]/25 text-[#0F2E2E]/60 active:bg-[#0F2E2E]/5 transition-colors"
                            >
                              <Plus
                                size={14}
                                strokeWidth={2}
                                className={`transition-transform duration-300 ${isMobileOpen ? "rotate-45" : ""}`}
                              />
                            </button>
                          </div>

                          {/* Desktop: description ALWAYS visible. */}
                          {row.desc && (
                            <p
                              className="hidden lg:block text-[14.5px] leading-relaxed text-[#0F2E2E]/70 pl-7 pr-2 max-w-2xl mt-2"
                              data-testid={`why-desc-${i}`}
                            >
                              {row.desc}
                            </p>
                          )}

                          {/* Mobile: description ANIMATES open on tap. */}
                          <AnimatePresence initial={false}>
                            {isMobileOpen && row.desc && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                className="overflow-hidden lg:hidden"
                              >
                                <p
                                  className="text-[14px] leading-relaxed text-[#0F2E2E]/72 pl-7 pr-2 pt-3"
                                  data-testid={`why-desc-mobile-${i}`}
                                >
                                  {row.desc}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* Mobile verdict strip — always visible so the
                              row conveys the ✓/✗ proof even when collapsed. */}
                          <div
                            className="mt-4 pl-7 flex items-center gap-4 lg:hidden"
                            data-testid={`why-verdict-mobile-${i}`}
                          >
                            <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-[#0F2E2E]">
                              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#0F2E2E]">
                                <Check size={12} strokeWidth={2.5} color="#FFFFFF" />
                              </span>
                              Riversol
                            </span>
                            <span className="w-px h-4 bg-[#0F2E2E]/20" />
                            <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-[#0F2E2E]/50">
                              <span className="flex items-center justify-center w-6 h-6 rounded-full border border-[#0F2E2E]/25">
                                <X size={12} strokeWidth={2} />
                              </span>
                              Others
                            </span>
                          </div>
                        </div>

                        {/* Desktop Riversol ✓ column */}
                        <div className="hidden lg:flex lg:justify-center">
                          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#0F2E2E]">
                            <Check size={13} strokeWidth={2.5} color="#FFFFFF" />
                          </span>
                        </div>

                        {/* Desktop Others ✗ column */}
                        <div className="hidden lg:flex lg:justify-center">
                          <span className="flex items-center justify-center w-7 h-7 rounded-full border border-[#0F2E2E]/25 text-[#0F2E2E]/35">
                            <X size={13} strokeWidth={2} />
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyRiversolV31;
